import _ from 'lodash'
import React, { ReactNode, useRef, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Typography,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import CustomTooltip from 'src/designSystem/CustomTooltip'
import { useSidebarControls } from 'src/designSystem/Container/sidebarControls'
import { FiltersIcon, PlusIcon } from 'src/designSystem/icons'
import { RoleSelectionChange, TagFilterAction } from './participantListAnalytics'
import { ROLE_FILTERS, ROLE_FILTER_LABELS, RoleFilter } from './roleFilterUtils'

const buttonSize = 36
const countSize = 20

type Props = {
  tags: Array<string>
  selectedTags: Array<string>
  selectedRoles?: Array<RoleFilter>
  onTagsChange: (tags: Array<string>, action: TagFilterAction) => void
  /** The role section is only offered when this is given. */
  onRolesChange?: (roles: Array<RoleFilter>, change: RoleSelectionChange) => void
  onClear: () => void
  controls?: ReactNode
  priorityFilters?: ReactNode
}

type SectionProps = {
  title: string
  onClose?: () => void
  children: ReactNode
}

const Section = ({ title, onClose, children }: SectionProps) => (
  <Box display="flex" flexDirection="column" gap={2}>
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography sx={(theme) => ({ color: theme.palette.darkestGreen, fontSize: 14, fontWeight: 600 })}>
        {title}
      </Typography>
      {onClose && (
        <IconButton aria-label="Close filters" onClick={onClose} sx={{ padding: 0 }}>
          <Close sx={(theme) => ({ color: theme.palette.darkestGreen })} />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
)

const FiltersMenu = ({
  tags,
  selectedTags,
  selectedRoles = [],
  onTagsChange,
  onRolesChange,
  onClear,
  controls,
  priorityFilters,
}: Props) => {
  const { isExpanded } = useSidebarControls()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)

  const count = selectedRoles.length + selectedTags.length
  const hasSelection = count > 0
  // A selected tag can vanish from the caseload after a refetch; keep it listed so it can be deselected.
  const listedTags = _.union(tags, selectedTags)

  const handleClose = () => setOpen(false)

  const toggleTag = (tag: string) => () =>
    selectedTags.includes(tag)
      ? onTagsChange(
          selectedTags.filter((selectedTag) => selectedTag !== tag),
          'remove'
        )
      : onTagsChange([...selectedTags, tag], 'add')

  const toggleRole = (role: RoleFilter) => () =>
    selectedRoles.includes(role)
      ? onRolesChange?.(
          selectedRoles.filter((selectedRole) => selectedRole !== role),
          { action: 'remove', role }
        )
      : onRolesChange?.([...selectedRoles, role], { action: 'add', role })

  return (
    <Box display="flex" alignItems="center" gap={2} paddingLeft={2} paddingRight={2}>
      {priorityFilters}
      <CustomTooltip title="Filters">
        <Button
          ref={anchorRef}
          aria-label={hasSelection ? `Filters, ${count} applied` : 'Filters'}
          color="inherit"
          onClick={() => setOpen((prevOpen) => !prevOpen)}
          sx={(theme) => ({
            alignItems: 'stretch',
            backgroundColor: 'common.white',
            border: `1px solid ${theme.palette.borderGrey}`,
            borderRadius: '8px',
            color: theme.palette.darkestGreen,
            fontSize: 12,
            fontWeight: 'normal',
            flexShrink: 0,
            height: buttonSize,
            minWidth: 0,
            padding: 0,
            textTransform: 'none',
            '&:hover': { backgroundColor: theme.palette.lightestGrey },
          })}
        >
          <Box
            component="span"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.5}
            padding={isExpanded || hasSelection ? 1 : 0}
            width={isExpanded || hasSelection ? undefined : buttonSize}
          >
            <FiltersIcon />
            {isExpanded && 'Filters'}
            {hasSelection && (
              <Box
                component="span"
                data-testid="filters-count"
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: `${countSize / 2}px`,
                  boxSizing: 'border-box',
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  height: countSize,
                  lineHeight: '19px',
                  minWidth: countSize,
                  padding: '0 6px 1px',
                  textAlign: 'center',
                })}
              >
                {count}
              </Box>
            )}
          </Box>
          <Box
            component="span"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={buttonSize}
            borderLeft={1}
            borderColor="borderGrey"
          >
            <PlusIcon />
          </Box>
        </Button>
      </CustomTooltip>
      <Box display="flex" marginLeft="auto">
        {controls}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { borderRadius: '8px', boxShadow: '0 8px 8px rgba(0, 0, 0, 0.25)' } }}
      >
        <Box display="flex" flexDirection="column" gap={3} padding={3} width={361} boxSizing="border-box">
          {onRolesChange && (
            <Section title="Filter by My Role" onClose={handleClose}>
              <FormGroup sx={{ gap: '18px' }}>
                {ROLE_FILTERS.map((role) => (
                  <FormControlLabel
                    key={role}
                    label={ROLE_FILTER_LABELS[role]}
                    control={
                      <Checkbox
                        color="primary"
                        checked={selectedRoles.includes(role)}
                        onChange={toggleRole(role)}
                        sx={{ padding: 0 }}
                      />
                    }
                    sx={{
                      gap: '9px',
                      marginLeft: 0,
                      marginRight: 0,
                      '& .MuiFormControlLabel-label': { fontSize: 16, lineHeight: '20px' },
                    }}
                  />
                ))}
              </FormGroup>
            </Section>
          )}

          <Section title="Select Tags" onClose={onRolesChange ? undefined : handleClose}>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {listedTags.length === 0 ? (
                <Chip label="No tags available" variant="outlined" />
              ) : (
                listedTags.map((tag) => {
                  const selected = selectedTags.includes(tag)
                  return (
                    <Chip
                      key={tag}
                      clickable
                      label={tag}
                      aria-pressed={selected}
                      color={selected ? 'primary' : undefined}
                      variant={selected ? undefined : 'outlined'}
                      onClick={toggleTag(tag)}
                      sx={(theme) => ({
                        borderRadius: '36px',
                        fontSize: 12,
                        fontWeight: 'normal',
                        height: 32,
                        ...(selected
                          ? {}
                          : { borderColor: theme.palette.borderGrey, color: theme.palette.darkestGreen }),
                      })}
                    />
                  )
                })
              )}
            </Box>
          </Section>

          <Box display="flex" justifyContent="flex-end" gap={1} paddingTop={1}>
            <Button variant="outlined" disabled={!hasSelection} onClick={onClear}>
              Clear
            </Button>
            <Button variant="contained" disabled={!hasSelection} onClick={handleClose}>
              Add filters
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default FiltersMenu
